import { noop } from 'lodash';
import { createContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import type { ReviewCardSuggestion } from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard.utils';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import type { KGV2NodeForGrouping } from '@/modules/aiSuggestions/kgV2Resolution.utils';

export interface AISuggestionReviewCardContext {
  /**
   * A map of suggestion ID to suggestion, of all unacknowledged suggestions in the household.
   */
  suggestionsByID: Record<string, AiSuggestionForReviewCard>;

  /**
   * KG V2 nodes referenced by the review suggestions' `principalNodeIDs`, keyed
   * by node ID. Used to resolve each grantor/owner/donor directly to its
   * data-model record via `resolvePrincipalsFromNodeMap`. Empty when the KG V2
   * feature flag is off.
   */
  principalNodesById: Map<string, KGV2NodeForGrouping>;

  /**
   * Whether the principal-node fetch is in flight. Create actions are disabled
   * while true so we never submit a suggestion before its grantors/owners
   * resolve (which would create the entity without principals, or drop it).
   */
  principalNodesLoading: boolean;

  /**
   * The suggestions that we want to display in the review card.
   */
  reviewSuggestions: ReviewCardSuggestion[];

  /**
   * The review suggestions that can be created without additional review.
   */
  creatableSuggestions: AiSuggestionForReviewCard[];

  /**
   * The suggestions that we don't want to display in the review card, but might
   * be referenced by one of the review suggestions (like a suggested principal
   * or business entity owner for an entity suggestion).
   */
  otherSuggestions: AiSuggestionForReviewCard[];

  /**
   * The function to call when the user clicks on the "Create" button.
   */
  onCreateClick: (suggestionID: string) => void;
  /**
   * The function to call when the user clicks on the "Create all" button.
   */
  onCreateAllClick: () => void;
  /**
   * The function to call when the user clicks on the "Review" button, or the "Magnifying Glass" (LocateSuggestionButton).
   */
  onReviewClick: (suggestionID: string) => void;
  /**
   * The function to call when the user clicks on the "Review all" button.
   */
  onReviewAllClick: () => void;
  /**
   * The function to call when the user clicks the "Delete" button.
   */
  onDeleteClick: (suggestionID: string) => void;
  /**
   * Hides a suggestion after it has been merged into an existing record.
   */
  onSuggestionMarkedDuplicate: (suggestionID: string) => void;
  /**
   * The list of suggestion IDs that are currently being deleted.
   */
  deletingSuggestionIds: string[];
  /**
   * The list of suggestion IDs that are currently being created.
   */
  creatingSuggestionIds: string[];
  /**
   * Whether a bulk "create all" operation is currently in progress.
   */
  isCreatingAll: boolean;
}

const getDefaultValues = (): AISuggestionReviewCardContext => ({
  suggestionsByID: {},
  principalNodesById: new Map(),
  principalNodesLoading: false,
  reviewSuggestions: [],
  creatableSuggestions: [],
  otherSuggestions: [],
  onCreateClick: noop,
  onCreateAllClick: noop,
  onReviewClick: noop,
  onReviewAllClick: noop,
  onDeleteClick: noop,
  onSuggestionMarkedDuplicate: noop,
  deletingSuggestionIds: [],
  creatingSuggestionIds: [],
  isCreatingAll: false,
});

export const AISuggestionReviewCardContext =
  createContext<AISuggestionReviewCardContext>(getDefaultValues());

export const useAISuggestionReviewCardContext = () => {
  return useGuardedContext(
    AISuggestionReviewCardContext,
    'AISuggestionReviewCardProvider'
  );
};
